import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  ShippingPackageType,
  CreateShippingPackagePayload,
  UpdateShippingPackagePayload,
  GetShippingPackagesParams,
  AssignDevicesPayload,
} from '../../types/inventory/shipping-package.types';
import { setNewOrderDetails, setNewOrderDetailsFetchStatus } from '../orders/orders.slice';
import { NewOrderType } from '../../types/order/order.type';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { getNewOrderDetails, getRMAOrderDetails } from '../orders/orders.action';
import { getFulfillmentOrderDetails } from './inventory.action';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';

export const getShippingPackages = createAsyncThunk(
  'inventory/getShippingPackages',
  async (params: GetShippingPackagesParams, thunkAPI) => {
    try {
      const { orderId, orderType } = params;
      const { data } = await axios.get<ShippingPackageType[]>(
        `${process.env.REACT_APP_API_URL}/inventory/shipping-package/order?orderId=${orderId}&orderType=${orderType}`
      );
      return data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Failed to fetch shipping packages' });
    }
  }
);

export const getShippingPackage = createAsyncThunk('inventory/getShippingPackage', async (id: number, thunkAPI) => {
  try {
    const { data } = await axios.get<ShippingPackageType>(
      `${process.env.REACT_APP_API_URL}/inventory/shipping-package/${id}`
    );
    return data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Failed to fetch shipping package' });
  }
});

export const createShippingPackage = createAsyncThunk<
  { id: number; index: number },
  {
    payload: CreateShippingPackagePayload;
    index: number;
  },
  { state: RootReducerState }
>('inventory/createShippingPackage', async ({ payload, index }, thunkAPI) => {
  try {
    const { data } = await axios.post<ShippingPackageType>(
      `${process.env.REACT_APP_API_URL}/inventory/shipping-package`,
      payload
    );

    if (payload.orderType === 'order') {
      const id = thunkAPI.getState().ordersReducer?.newOrderDetails?.id;
      if (id) thunkAPI.dispatch(getNewOrderDetails({ id }));
    }

    return { id: data.id, index };
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Failed to create shipping package' });
  }
});

export const updateShippingPackage = createAsyncThunk<
  ShippingPackageType,
  {
    payload: UpdateShippingPackagePayload;
    orderType: string;
  },
  { state: RootReducerState }
>('inventory/updateShippingPackage', async ({ payload, orderType }, thunkAPI) => {
  try {
    const { id, ...rest } = payload;

    const { data } = await axios.put<ShippingPackageType>(
      `${process.env.REACT_APP_API_URL}/inventory/shipping-package/${id}`,
      rest
    );

    if (orderType === 'order') {
      const id = thunkAPI.getState().ordersReducer?.newOrderDetails?.id;
      if (id) thunkAPI.dispatch(getNewOrderDetails({ id }));
    } else if (orderType === 'fulfillment_order') {
      const fulfillmentOrderId = thunkAPI.getState().inventoryReducer.fulfillmentOrderDetails?.id;
      if (fulfillmentOrderId) {
        thunkAPI.dispatch(getFulfillmentOrderDetails({ id: fulfillmentOrderId }));
      }
    } else if (orderType === 'rma_order') {
      const id = thunkAPI.getState().ordersReducer?.rmaOrderDetails?.id;
      if (id) thunkAPI.dispatch(getRMAOrderDetails({ id }));
    }

    return data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Failed to update shipping package' });
  }
});

export const assignDevicesToPackage = createAsyncThunk(
  'inventory/assignDevicesToPackage',
  async (payload: AssignDevicesPayload, thunkAPI) => {
    try {
      const { id, deviceIds } = payload;
      const { data } = await axios.put<ShippingPackageType>(
        `${process.env.REACT_APP_API_URL}/inventory/shipping-package/${id}`,
        { deviceIds }
      );
      return data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Failed to assign devices to package' });
    }
  }
);

export const deleteShippingPackage = createAsyncThunk<
  number,
  { id: number; orderId?: number; fulfillmentId?: number; rmaOrderId?: number },
  { state: RootReducerState }
>('inventory/deleteShippingPackage', async ({ id, rmaOrderId }, thunkAPI) => {
  try {
    await axios.delete(`${process.env.REACT_APP_API_URL}/inventory/shipping-package/${id}`);

    if (rmaOrderId) thunkAPI.dispatch(getRMAOrderDetails({ id: rmaOrderId }));

    return id;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Failed to delete shipping package' });
  }
});
